import heroImage from "./article_pics/lemurloot.jpg";
// src/articles/lemurloot.js
const article = {
    title:
        "Emulating LEMURLOOT (MOVEit) and Detecting It with Zeek — A Copy-Paste Lab",
    published: "2025-08-17",
    readingTime: "24 min read",
    tags: [
        "Zeek",
        "Threat Emulation",
        "MOVEit",
        "FIN11",
        "DPD",
        "Detection Engineering",
    ],
    summary:
        "Everything in one place: a tiny mock server, a PowerShell traffic generator, a Wireshark filter, a Zeek script that raises clear notices.",
    favorite: false,
    // kept as a relative path; callers will require() it
    image: heroImage,
    repoUrl: "https://github.com/Pb-22/LEMURLOOT_Lab",
    content: `
If you like hands-on detection engineering, this walkthrough gives you **everything you need** to emulate the LEMURLOOT web-shell behavior (seen during the MOVEit intrusions) and **detect it cleanly with Zeek**. You’ll get:

- a tiny mock HTTP server that behaves like LEMURLOOT,
- a PowerShell script that drives realistic header-based commands,
- a Wireshark display filter to validate your PCAP,
- a Zeek script that raises two simple, high-signal notices:
  - **LEMURLOOT_Handshake** — after the GUID “handshake”
  - **LEMURLOOT_CmdGzip** — when commands are sent and the response is gzip
- a **Cleanup** section to stop/remove the server cleanly when you’re done

This exercise is based on the public analysis by Google Cloud / Mandiant:  
**“Zero-Day Vulnerability in MOVEit Transfer Exploited for Data Theft.”**  
<https://cloud.google.com/blog/topics/threat-intelligence/zero-day-moveit-data-theft>

**Lab-only!** The mock server is intentionally minimal and meant strictly for **contained testing**.

---

## What we’re modeling (plain language)

The LEMURLOOT web shell uses **HTTP headers** rather than obvious URLs/parameters:

- The client includes a **GUID** in header \`X-siLock-Comment\`.
- If the GUID is correct, the server **echoes** \`X-siLock-Comment: comment\`. That’s the handshake.
- Commands ride in headers \`X-siLock-Step1\`, \`X-siLock-Step2\`, \`X-siLock-Step3\`.
- Replies are **gzip** encoded.

Those two behaviors — **handshake echo** and **gzip-wrapped responses** after Step headers — are stable, high-signal patterns we can detect at the HTTP layer with a short Zeek script.

---

## Lab topology

- **Ubuntu VM** → mock server on \`0.0.0.0:8002\` at \`/human2.aspx\`.
- **Windows workstation** → traffic driver with PowerShell/\`curl.exe\`.
- **Optional capture** → use Wireshark to record a PCAP and test on **try.zeek.org** with the script.

---

## Step 1 — Start the mock server (Ubuntu)

The script below installs Python (if needed), creates a small HTTP handler, and registers a systemd service.

**What it does**

- Returns **404** unless the path is \`/human2.aspx\`.
- Requires header \`X-siLock-Comment: <GUID>\` to pass the gate.
- Echoes \`X-siLock-Comment: comment\` on success (the handshake).
- Responds to Step headers and **gzip-encodes** the body.

Save as **\`setup_lemurloot_server.sh\`** on the Ubuntu VM and run: \`sudo bash setup_lemurloot_server.sh\`.

\`\`\`bash
#!/usr/bin/env bash
set -euo pipefail

apt-get update -y
apt-get install -y python3 python3-venv

useradd -r -s /usr/sbin/nologin lemur 2>/dev/null || true
install -d -o lemur -g lemur /opt/lemurloot

cat >/opt/lemurloot/mock_lemurloot_server.py <<'PY'
from http.server import BaseHTTPRequestHandler, HTTPServer
import gzip, io
HOST='0.0.0.0'; PORT=8002; PATH='/human2.aspx'
GUID='11111111-1111-1111-1111-111111111111'
GUID_HEADER='X-siLock-Comment'; STEP1='X-siLock-Step1'; STEP2='X-siLock-Step2'; STEP3='X-siLock-Step3'
BANNER=b'handshake-ok'

class H(BaseHTTPRequestHandler):
    def _send(self, code=200, body=b'OK', gzip_body=True, echo=False):
        data=body
        if gzip_body:
            buf=io.BytesIO()
            with gzip.GzipFile(fileobj=buf, mode='wb') as z: z.write(body)
            data=buf.getvalue()
        self.send_response(code)
        if echo: self.send_header(GUID_HEADER, 'comment')
        self.send_header('Content-Type','application/octet-stream')
        if gzip_body: self.send_header('Content-Encoding','gzip')
        self.send_header('Content-Length', str(len(data)))
        self.end_headers()
        self.wfile.write(data)

    def do_GET(self):
        if self.path!=PATH: self.send_error(404); return
        guid=self.headers.get(GUID_HEADER)
        if guid!=GUID: self.send_error(404); return
        self._send(200, BANNER, True, True)

    def do_POST(self):
        if self.path!=PATH: self.send_error(404); return
        guid=self.headers.get(GUID_HEADER)
        if guid!=GUID: self.send_error(404); return
        s1=self.headers.get(STEP1); s2=self.headers.get(STEP2); s3=self.headers.get(STEP3)
        if s1=='-1': body=b'azure-settings-and-queries'
        elif s1=='-2': body=b'delete-user Health Check Service'
        elif s2 or s3: body=b'file-fetch step2/step3'
        else: body=b'noop'
        self._send(200, body, True, True)

if __name__=='__main__':
    print(f'[*] Listening on {HOST}:{PORT}{PATH}')
    HTTPServer((HOST, PORT), H).serve_forever()
PY

chown lemur:lemur /opt/lemurloot/mock_lemurloot_server.py

cat >/etc/systemd/system/lemurloot-mock.service <<'UNIT'
[Unit]
Description=Mock LEMURLOOT server
After=network.target

[Service]
User=lemur
ExecStart=/usr/bin/python3 /opt/lemurloot/mock_lemurloot_server.py
Restart=on-failure

[Install]
WantedBy=multi-user.target
UNIT

systemctl daemon-reload
systemctl enable --now lemurloot-mock.service
echo '[+] Mock server on 0.0.0.0:8002/human2.aspx'
\`\`\`

**What running looks like**

![Mock server running on 0.0.0.0:8002/human2.aspx](${require("./article_pics/lemur_server_output.png")})

---

## Step 2 — Generate traffic (Windows PowerShell)

> **Important:** Replace the placeholder below with **your Ubuntu server IP**.

\\\`\\\`\\\`powershell
$S = "<YOUR_UBUNTU_SERVER_IP>"
$G = "11111111-1111-1111-1111-111111111111"
$URL = "http://$S:8002/human2.aspx"

Write-Host "== Probe (404 expected) ==" -ForegroundColor Cyan
curl.exe -i $URL

Write-Host "\\n== Handshake-only ==" -ForegroundColor Cyan
curl.exe -i -H "X-siLock-Comment: $G" $URL

Write-Host "\\n== Handshake + Step1 = -1 ==" -ForegroundColor Cyan
curl.exe -i -X POST -H "X-siLock-Comment: $G" -H "X-siLock-Step1: -1" $URL

Write-Host "\\n== Delete-user (Step1 = -2) ==" -ForegroundColor Cyan
curl.exe -i -X POST -H "X-siLock-Comment: $G" -H "X-siLock-Step1: -2" $URL

Write-Host "\\n== File fetch (Step2/Step3) ==" -ForegroundColor Cyan
curl.exe -i -X POST -H "X-siLock-Comment: $G" -H "X-siLock-Step2: 123" -H "X-siLock-Step3: 456" $URL
\\\`\\\`\\\`

**What the driving looks like**

![PowerShell ISE with script and output](${require("./article_pics/lemur_powershell_ise.png")})

---

## Step 2.5 — Validate with Wireshark

Use either of these **display filters** to zero in on the traffic:

**Targeted to this lab (path/headers/gzip + your server IP):**
\`\`\`
ip.addr == <YOUR_UBUNTU_SERVER_IP> && http &&
(http.request.uri == "/human2.aspx" ||
 http contains "X-siLock-Comment" ||
 http contains "X-siLock-Step" ||
 http.content_encoding == "gzip")
\`\`\`

**Port-centric (if you kept 8002):**
\`\`\`
tcp.port == 8002 && http
\`\`\`

You should see:
- A handshake request with \`X-siLock-Comment: <GUID>\`.
- A server response echoing \`X-siLock-Comment: comment\`.
- POSTs carrying \`X-siLock-Step*\` headers.
- Responses with \`Content-Encoding: gzip\`.

![Wireshark with the display filter applied](${require("./article_pics/lemur_wireshark_filter.png")})

---

## Step 3 — Detect it with Zeek (complete script)

> ### Quick note on terminology: “script” vs “policy script”
> In Zeek, any \`.zeek\` file is a **script**. A **policy script** is a script that expresses *what to alert/log and when* (e.g., using \`NOTICE()\`, custom \`Notice::Type\`, and \`&redef\` options).  
> The file below is a **policy script** because it encodes detection behavior and alerting rules, but throughout the article we simply call it a **script** for readability.

Paste into **\`main.zeek\`** (or your favorite policy file):

\`\`\`zeek
@load base/frameworks/notice
@load base/protocols/http
@load base/frameworks/analyzer/dpd

module LEMURLOOT;

export {
    redef enum Notice::Type += { LEMURLOOT_Handshake, LEMURLOOT_CmdGzip };

    option force_http_port_registration = F &redef;

    const forced_http_ports: set[port] = {
        80/tcp, 8080/tcp, 8088/tcp, 8880/tcp, 8881/tcp, 8888/tcp,
        8010/tcp, 8020/tcp, 8021/tcp, 8028/tcp, 8002/tcp
    } &redef;

    const GUID_PAT: pattern = /[0-9A-Fa-f]{8}-([0-9A-Fa-f]{4}-){3}[0-9A-Fa-f]{12}$/ &redef;
    const GZIP_PAT: pattern = /gzip/i &redef;

    option alert_on_handshake_only = T &redef;

    redef Notice::type_suppression_intervals += {
        [LEMURLOOT_Handshake] = 5mins,
        [LEMURLOOT_CmdGzip]   = 5mins
    };
}

event zeek_init() {
    if ( force_http_port_registration && |forced_http_ports| > 0 )
        Analyzer::register_for_ports(Analyzer::ANALYZER_HTTP, LEMURLOOT::forced_http_ports);
}

global hs_req_seen: table[string] of bool   &default=F;
global hs_guid:     table[string] of string &default="";
global ack_seen:    table[string] of bool   &default=F;
global resp_gzip:   table[string] of bool   &default=F;
global pending_cmd: table[string] of string;

function try_fire_cmd(c: connection) {
    local uid = c$uid;
    if ( resp_gzip[uid] && uid in pending_cmd && hs_req_seen[uid] && ack_seen[uid] ) {
        local details = pending_cmd[uid];
        local guid = (uid in hs_guid) ? hs_guid[uid] : "unknown-guid";

        local n: Notice::Info = [
            $note       = LEMURLOOT_CmdGzip,
            $msg        = fmt("LEMURLOOT-style command+gzip on %s -> %s (uid=%s, guid=%s) %s",
                               c$id$orig_h, c$id$resp_h, uid, guid, details),
            $conn       = c,
            $identifier = cat("LL-CMD|", uid)
        ];
        NOTICE(n);

        delete pending_cmd[uid];
        resp_gzip[uid] = F;
    }
}

event http_header(c: connection, is_orig: bool, original_name: string, name: string, value: string) {
    local uid = c$uid;

    if ( is_orig ) {
        if ( name == "X-SILOCK-COMMENT" && value in GUID_PAT ) {
            hs_req_seen[uid] = T;
            hs_guid[uid] = value;
        }

        if ( name == "X-SILOCK-STEP1" || name == "X-SILOCK-STEP2" || name == "X-SILOCK-STEP3" ) {
            local prev = (uid in pending_cmd ? pending_cmd[uid] : "");
            local key = (name == "X-SILOCK-STEP1" ? "Step1" :
                        (name == "X-SILOCK-STEP2" ? "Step2" : "Step3"));
            local piece = fmt("%s=%s", key, value);
            pending_cmd[uid] = (|prev| > 0 ? fmt("%s,%s", prev, piece) : piece);
        }
    } else {
        if ( name == "X-SILOCK-COMMENT" && to_lower(value) == "comment" ) {
            ack_seen[uid] = T;
            if ( LEMURLOOT::alert_on_handshake_only && hs_req_seen[uid] ) {
                local guid = (uid in hs_guid ? hs_guid[uid] : "unknown-guid");
                local n: Notice::Info = [
                   $note       = LEMURLOOT_Handshake,
                   $msg        = fmt("LEMURLOOT-style handshake on %s -> %s (uid=%s, guid=%s)",
                                      c$id$orig_h, c$id$resp_h, uid, guid),
                   $conn       = c,
                   $identifier = cat("LL-HS|", uid)
                ];
                NOTICE(n);
            }
        }

        if ( name == "CONTENT-ENCODING" && value in GZIP_PAT ) {
            resp_gzip[uid] = T;
            try_fire_cmd(c);
        }
    }
}

event connection_state_remove(c: connection) {
    local uid = c$uid;
    delete hs_req_seen[uid];
    if ( uid in hs_guid ) delete hs_guid[uid];
    delete ack_seen[uid];
    delete resp_gzip[uid];
    if ( uid in pending_cmd ) delete pending_cmd[uid];
}
\`\`\`

---

## Try it on \`try.zeek.org\`

1. Paste the script into the left pane.
2. Upload your PCAP.
3. Run. You should see \`LEMURLOOT_Handshake\` and/or \`LEMURLOOT_CmdGzip\` in **notice.log**.

![try.zeek.org showing notices in the output](${require("./article_pics/lemur_tryzeek_notices.png")})

---

## Cleanup

Because the setup script installed a **systemd** service (\`lemurloot-mock.service\`), managing it is simple.

### Stop it now
\`\`\`bash
sudo systemctl stop lemurloot-mock.service
\`\`\`

### Prevent it from starting on boot (optional)
\`\`\`bash
sudo systemctl disable lemurloot-mock.service
\`\`\`

### Verify it’s really stopped
\`\`\`bash
systemctl is-active lemurloot-mock.service   # should print "inactive"
sudo ss -ltnp | grep :8002 || echo "Port 8002 is closed"
# or
sudo lsof -i :8002 || echo "Nothing is listening on 8002"
\`\`\`

### Peek at recent service logs (optional)
\`\`\`bash
journalctl -u lemurloot-mock.service -n 50 --no-pager
\`\`\`

### Full clean-up / uninstall (optional)
This removes the service unit, script files, and the dedicated service user created by the setup script.

\`\`\`bash
sudo systemctl stop lemurloot-mock.service
sudo systemctl disable lemurloot-mock.service
sudo rm /etc/systemd/system/lemurloot-mock.service
sudo systemctl daemon-reload
sudo rm -rf /opt/lemurloot
sudo userdel -r lemur || true
\`\`\`

> **Re-enable later?**  
> If you only disabled the service (did not delete files/unit), you can bring it back with:
> \`\`\`bash
> sudo systemctl enable --now lemurloot-mock.service
> \`\`\`

---

## Files included (for convenience)

- **Mock server**: \`setup_lemurloot_server.sh\` (Ubuntu)  
- **Traffic harness**: \`lemurloot_test.ps1\` (Windows) — **remember to set** \`$S = "<YOUR_UBUNTU_SERVER_IP>"\`  
- **Detector**: \`FIN11_LEMURLOOT.zeek\` (script above)

Happy hunting!
`,
};

export default article;
